import type { FormField } from '@/models/form';
import { sameUnitAnswer, wireAnswer, type UnmatchedValue } from '@/models/scannedValues';

/**
 * What applying a scan wrote into the open entry, held outside `form.items` as `transientMarks.ts` holds its marks, so
 * the deep watch there and the save body never see it. It is per-entry state (`freshEntryState()`), so a reload, the
 * re-read after a save, or opening another entry drops it.
 *
 * Three things read it: the "Scanned" chip, which lasts until the field changes; Undo, which puts back each field the
 * reader hasn't changed since; and the record sent at Save (ADR 0016), which names the fields still holding what the
 * scan wrote, with the exact answers they save.
 */

/** One field an apply wrote. */
export interface AppliedField {
  identifier: string;
  variable: string;
  /** The answer the field saved right after the apply, as `toWire` writes it. */
  answer: string;
  /** The answer keys the field held before the apply, which Undo writes back. */
  previous: Partial<FormField>;
}

/** One apply of one scan. */
export interface ScanApply {
  scanId: string;
  fields: AppliedField[];
  /** The fields not changed since the apply: the ones that draw the chip and that Undo puts back. */
  unchanged: ReadonlySet<string>;
  /**
   * The entry and answers last recorded for this apply (`recordKey`), so a second Save doesn't record the same again.
   * The entry is part of it because a new entry's id is made afresh by each Save until a create lands.
   */
  recorded?: string;
}

/** The banner over the fields after an apply: how many values went in, and the ones that didn't, with why. */
export interface ScanBanner {
  filled: number;
  unmatched: UnmatchedValue[];
}

/** The answer keys a unit field holds, which an apply overwrites and Undo writes back. */
export const answerKeys = ({ numberValue, value, choiceValue }: FormField): Partial<FormField> =>
  ({ numberValue, value, choiceValue });

/** The fields that draw the "Scanned" chip: written by an apply and not changed since. */
export const scannedFieldIds = (applies: readonly ScanApply[]): Set<string> =>
  new Set(applies.flatMap(apply => [...apply.unchanged]));

/** `applies` with `identifiers` no longer counted as unchanged by any of them: the reader or a later apply changed them. */
export const withChanged = (applies: readonly ScanApply[], identifiers: readonly string[]): ScanApply[] =>
  applies.map(apply => {
    if (!identifiers.some(identifier => apply.unchanged.has(identifier))) return apply;
    const unchanged = new Set(apply.unchanged);
    identifiers.forEach(identifier => unchanged.delete(identifier));
    return { ...apply, unchanged };
  });

/**
 * `applies` with each of `identifiers` given back to an earlier apply whose answer the field holds again, as it does
 * once Undo puts back what a later apply overwrote.
 */
export const withRestored = (
  applies: readonly ScanApply[],
  identifiers: readonly string[],
  fields: readonly FormField[]
): ScanApply[] =>
  applies.map(apply => {
    const restored = apply.fields.filter(({ identifier, answer }) => {
      const field = identifiers.includes(identifier) && fields.find(item => item.identifier === identifier);
      return !!field && sameUnitAnswer(wireAnswer(field), answer);
    });
    if (restored.length === 0) return apply;
    return { ...apply, unchanged: new Set([...apply.unchanged, ...restored.map(({ identifier }) => identifier)]) };
  });

/** The fields Undo writes back for `apply`: those not changed since, each with what it held before. */
export const undoPatches = (apply: ScanApply): AppliedField[] =>
  apply.fields.filter(({ identifier }) => apply.unchanged.has(identifier));

/** What one application records (ENG-10005): the variables still holding what the scan wrote, and their answers. */
export interface ScanRecord {
  /** The apply's place in the entry's list, so a record that went through can be marked on it. */
  apply: number;
  scanId: string;
  variables: string[];
  /** Each variable's answer exactly as the entry saves it. Its keys are `variables`. */
  answers: Record<string, string>;
}

/** Where Save records an application: the saved entry, its form, its case, and the study as the pump routes name it. */
export interface ScanRecordTarget {
  entryId: string;
  formId: string;
  caseId: string;
  pumpCenterId: string;
}

/** What `ScanApply.recorded` holds for a record of `answers` against `entryId`. */
export const recordKey = (entryId: string, answers: Record<string, string>): string =>
  JSON.stringify({ entryId, answers });

/**
 * What to record at Save for each apply against `entryId`: the fields whose answer still says what the scan wrote, the
 * way spdata-api compares them, with the answer the field saves now. An apply none of whose values survived records
 * nothing, and one whose answers were recorded already against this entry isn't recorded again.
 */
export const scanRecords = (
  applies: readonly ScanApply[],
  fields: readonly FormField[],
  entryId: string
): ScanRecord[] =>
  applies.flatMap((apply, index) => {
    const answers: Record<string, string> = {};
    for (const { identifier, variable, answer } of apply.fields) {
      const field = fields.find(item => item.identifier === identifier);
      const now = field && wireAnswer(field);
      if (typeof now === 'string' && sameUnitAnswer(now, answer)) {
        answers[variable] = now;
      }
    }
    const variables = Object.keys(answers);
    if (variables.length === 0 || apply.recorded === recordKey(entryId, answers)) return [];
    return [{ apply: index, scanId: apply.scanId, variables, answers }];
  });
